import React, { useState } from "react";
import { ArrowLeft, Archive, Eye, FileDown, Loader2 } from "lucide-react";
import { moduleApi } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { mapReservationFromApi, type Board } from "./types";
import { exportPdf } from "./exportUtils";

export function ArchivedBoards({
  boards,
  onBack,
  onOpenBoard,
}: {
  boards: Board[];
  onBack: () => void;
  onOpenBoard: (id: number) => void;
}) {
  const { user } = useAuth();
  const archivedBoards = boards.filter((b) => b.isArchived);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  const handleDownloadPdf = async (board: Board) => {
    setDownloadingId(board.id);
    try {
      const data = await moduleApi.getExceptionServiceBoardReservations(board.id);
      const reservations = Array.isArray(data) ? data.map((r: any) => mapReservationFromApi(r, board.title)) : [];
      await exportPdf(board, reservations, user?.theme_color || undefined);
    } catch (e) {
      console.error("Failed to download board PDF", e);
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="space-y-8">
      <header className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-2xl bg-brand-500/15 flex items-center justify-center text-brand-500">
          <Archive size={22} />
        </div>
        <div>
          <h1 className="text-2xl font-black text-content">Tableaux Archivés</h1>
          <p className="text-sm text-content-muted mt-1">Consultation seule — un tableau archivé ne peut plus être modifié ni désarchivé.</p>
        </div>
      </header>

      <button onClick={onBack} className="flex items-center gap-2 text-sm font-bold text-content-muted hover:text-content transition-colors">
        <ArrowLeft size={16} /> Retour aux tableaux actifs
      </button>

      {archivedBoards.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-border rounded-3xl bg-surface/40">
          <Archive size={40} className="mx-auto mb-3 text-content-muted opacity-40" />
          <p className="text-content-muted font-medium">Aucun tableau archivé.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {archivedBoards.map((board) => (
            <div key={board.id} className="bg-surface border border-border rounded-2xl p-6 shadow-sm">
              <h2 className="text-lg font-bold text-content mb-1">{board.title}</h2>
              <p className="text-xs text-content-muted mb-6">Créé le {board.createdAt}</p>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => onOpenBoard(board.id)}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-surface-soft hover:bg-border text-content text-sm font-bold transition-all"
                >
                  <Eye size={14} /> Consulter
                </button>
                <button
                  onClick={() => handleDownloadPdf(board)}
                  disabled={downloadingId === board.id}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-surface-soft hover:bg-border text-content text-sm font-bold transition-all disabled:opacity-60"
                >
                  {downloadingId === board.id ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />} PDF
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
